import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  FocusStore,
  HabitStore,
  JournalStore,
  ListStore,
  MoneyTrackerStore,
  NoteStore,
  TodoStore,
  ViewSettingsStore
} from '@app/_stores';
import { AlertService } from '@app/_services/alert.service';
import { SettingsService } from '@app/_services/settings.service';
import { CacheService } from '@app/_services/cache.service';
import { ModalService } from '@app/_services/modal.service';
import { getLocalDateString as getDateString } from '@app/_services/date';
import { log } from '@app/_services/logger';
import { LoaderComponent } from '@app/components/shared/loader/loader.component';

/**
 * SharedDeeplinkComponent handles deeplinks from shared email notifications
 *
 * URL Format: /shared?collection={type}&id={id}&inviteToken={token}
 *
 * Supported collections:
 * - todos
 * - notes
 * - lists
 * - habitSetups
 * - journalSetups
 * - moneyTrackerSetups
 * - focus
 * - messageSetups
 */
@Component({
  selector: 'app-shared-deeplink',
  standalone: true,
  imports: [CommonModule, LoaderComponent],
  template: `
    <div class="shared-deeplink-container">
      <app-loader></app-loader>
    </div>
  `,
  styles: [`
    .shared-deeplink-container {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100vh;
      width: 100%;
    }
  `]
})
export class SharedDeeplinkComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly todoStore = inject(TodoStore);
  private readonly noteStore = inject(NoteStore);
  private readonly listStore = inject(ListStore);
  private readonly habitStore = inject(HabitStore);
  private readonly journalStore = inject(JournalStore);
  private readonly moneyTrackerStore = inject(MoneyTrackerStore);
  private readonly focusStore = inject(FocusStore);
  private readonly viewSettingsStore = inject(ViewSettingsStore);
  private readonly alertService = inject(AlertService);
  private readonly ss = inject(SettingsService);
  private readonly cc = inject(CacheService);
  private readonly modalService = inject(ModalService);

  private unSubscribe = new Subject<void>();

  ngOnInit() {
    this.route.queryParamMap.pipe(takeUntil(this.unSubscribe)).subscribe(async params => {
      const collection = params.get('collection');
      const id = params.get('id');
      const inviteToken = params.get('inviteToken');

      log.debug('SharedDeeplinkComponent', 'Processing deeplink', { collection, id, inviteToken });

      if (!collection || !id) {
        log.warn('SharedDeeplinkComponent', 'Missing collection or id');
        await this.showNotFoundAndRedirect();
        return;
      }

      // Wait for user authentication
      await this.waitForAuthReady();

      // Handle deeplink - each handler will wait for its specific store
      await this.handleDeeplink(collection, id, inviteToken);
    });
  }

  /**
   * Wait for user authentication to be ready
   */
  private async waitForAuthReady(): Promise<void> {
    return new Promise(resolve => {
      const maxWaitTime = 3000;
      const checkInterval = 100;
      let elapsed = 0;

      const intervalId = setInterval(() => {
        const isReady = this.cc.user?.uid;

        if (isReady || elapsed >= maxWaitTime) {
          clearInterval(intervalId);
          resolve();
        }

        elapsed += checkInterval;
      }, checkInterval);
    });
  }

  /**
   * Wait for a specific store to have data loaded
   * Returns true if data loaded, false if timeout
   */
  private async waitForStoreData(checkFn: () => boolean): Promise<boolean> {
    return new Promise(resolve => {
      const maxWaitTime = 8000; // 8 seconds - enough time for IndexedDB to load
      const checkInterval = 100;
      let elapsed = 0;

      const intervalId = setInterval(() => {
        const hasData = checkFn();

        if (hasData) {
          clearInterval(intervalId);
          log.debug('SharedDeeplinkComponent', 'Store data loaded', { elapsed });
          resolve(true);
        } else if (elapsed >= maxWaitTime) {
          clearInterval(intervalId);
          log.warn('SharedDeeplinkComponent', 'Store data timeout', { elapsed });
          resolve(false);
        }

        elapsed += checkInterval;
      }, checkInterval);
    });
  }

  private async handleDeeplink(collection: string, id: string, inviteToken: string | null) {
    switch (collection) {
      case 'todos':
        await this.openTodo(id);
        break;
      case 'notes':
        await this.openNote(id);
        break;
      case 'lists':
        await this.openList(id);
        break;
      case 'habitSetups':
        await this.openHabitSetup(id);
        break;
      case 'journalSetups':
        await this.openJournalSetup(id);
        break;
      case 'moneyTrackerSetups':
        await this.openMoneyTrackerSetup(id);
        break;
      case 'focus':
        await this.openFocus(id);
        break;
      case 'messageSetups':
        await this.openMessageSetup(id);
        break;
      default:
        log.warn('SharedDeeplinkComponent', 'Unknown collection type', { collection });
        await this.showNotFoundAndRedirect();
    }
  }

  private async openTodo(id: string) {
    // Wait for todo store to load data
    const hasData = await this.waitForStoreData(() => {
      const todos = Object.keys(this.todoStore.idToTodo());
      return todos.length > 0;
    });

    const todo = this.todoStore.idToTodo()[id];
    if (todo && !todo.deletedAt) {
      // Navigate to today first, then open modal
      this.router.navigate(['/today']).then(async () => {
        const { TodoFormComponent } = await import('@app/components/features/todos/todo-form/todo-form.component');
        this.modalService.openLevel1(TodoFormComponent, {
          mode: 'edit',
          value: todo,
        }, { disableClose: true });
        // Enable (unhide) todo feature if hidden - called AFTER opening modal
        if (this.viewSettingsStore.enableFeatureIfHidden('todo')) {
          this.viewSettingsStore.persist();
        }
      });
    } else {
      await this.showNotFoundAndRedirect('todo');
    }
  }

  private async openNote(id: string) {
    // Wait for note store to load data
    await this.waitForStoreData(() => {
      const notes = Object.keys(this.noteStore.idToNote());
      return notes.length > 0;
    });

    const note = this.noteStore.idToNote()[id];
    if (note && !note.deletedAt) {
      this.router.navigate(['/today']).then(async () => {
        const { NoteFormComponent } = await import('@app/components/features/notes/note-form/note-form.component');
        this.modalService.openLevel1(NoteFormComponent, {
          mode: 'edit',
          value: note,
        }, { disableClose: true });
        // Enable (unhide) note feature if hidden - called AFTER opening modal
        if (this.viewSettingsStore.enableFeatureIfHidden('note')) {
          this.viewSettingsStore.persist();
        }
      });
    } else {
      await this.showNotFoundAndRedirect('note');
    }
  }

  private async openList(id: string) {
    // Wait for list store to load data
    await this.waitForStoreData(() => {
      const lists = Object.keys(this.listStore.idToList());
      return lists.length > 0;
    });

    const list = this.listStore.idToList()[id];
    if (list && !list.deletedAt) {
      this.router.navigate(['/lists']).then(async () => {
        const { ListItemsComponent } = await import('@app/components/features/lists/list-items/list-items.component');
        this.modalService.openLevel1(ListItemsComponent, {
          value: list
        }, { disableClose: true, autoFocus: false });
        // Enable (unhide) list feature if hidden - called AFTER opening modal
        if (this.viewSettingsStore.enableFeatureIfHidden('list')) {
          this.viewSettingsStore.persist();
        }
      });
    } else {
      await this.showNotFoundAndRedirect('list');
    }
  }

  private async openHabitSetup(id: string) {
    // Wait for habit store to load data
    await this.waitForStoreData(() => {
      const setups = Object.keys(this.habitStore.idToSetup());
      return setups.length > 0;
    });

    const habitSetup = this.habitStore.idToSetup()[id];
    if (habitSetup && !habitSetup.deletedAt) {
      this.router.navigate(['/today']).then(async () => {
        const { HabitSetupFormComponent } = await import('@app/components/features/habits/habit-setup-form/habit-setup-form.component');
        const data = {
          mode: 'edit',
          value: habitSetup,
        };
        if (this.cc.isNarrowScreen()) {
          this.modalService.openMainContent(HabitSetupFormComponent, data, { disableClose: true, autoFocus: false });
        } else {
          this.modalService.openLevel1(HabitSetupFormComponent, data, { disableClose: true, autoFocus: false });
        }
        // Enable (unhide) habit if hidden - called AFTER opening modal
        if (this.viewSettingsStore.enableFeatureIfHidden('habit', habitSetup.id)) {
          this.viewSettingsStore.persist();
        }
      });
    } else {
      await this.showNotFoundAndRedirect('habit');
    }
  }

  private async openJournalSetup(id: string) {
    // Wait for journal store to load data
    await this.waitForStoreData(() => {
      const setups = Object.keys(this.journalStore.idToSetup());
      return setups.length > 0;
    });

    const journalSetup = this.journalStore.idToSetup()[id];
    if (journalSetup && !journalSetup.deletedAt) {
      this.router.navigate(['/today']).then(async () => {
        const { JournalSetupFormComponent } = await import('@app/components/features/journals/journal-setup-form/journal-setup-form.component');
        const data = {
          mode: 'edit',
          value: journalSetup,
        };
        if (this.cc.isNarrowScreen()) {
          this.modalService.openMainContent(JournalSetupFormComponent, data, { disableClose: true, autoFocus: false });
        } else {
          this.modalService.openLevel1(JournalSetupFormComponent, data, { disableClose: true, autoFocus: false });
        }
        // Enable (unhide) journal if hidden - called AFTER opening modal
        if (this.viewSettingsStore.enableFeatureIfHidden('journal', journalSetup.id)) {
          this.viewSettingsStore.persist();
        }
      });
    } else {
      await this.showNotFoundAndRedirect('journal');
    }
  }

  private async openMoneyTrackerSetup(id: string) {
    // Wait for money tracker store to load data
    await this.waitForStoreData(() => {
      const setups = Object.keys(this.moneyTrackerStore.idToSetup());
      return setups.length > 0;
    });

    const setup = this.moneyTrackerStore.idToSetup()[id];
    if (setup && !setup.deletedAt) {
      this.router.navigate(['/today']).then(async () => {
        const { MoneyTrackerSetupFormComponent } = await import('@app/components/features/money-tracker/money-tracker-setup-form/money-tracker-setup-form.component');
        const data = {
          mode: 'edit',
          value: setup,
        };
        if (this.cc.isNarrowScreen()) {
          this.modalService.openMainContent(MoneyTrackerSetupFormComponent, data, { disableClose: true, autoFocus: false });
        } else {
          this.modalService.openLevel1(MoneyTrackerSetupFormComponent, data, { disableClose: true, autoFocus: false });
        }
        // Enable (unhide) money tracker if hidden - called AFTER opening modal
        if (this.viewSettingsStore.enableFeatureIfHidden('moneyTracker', setup.id)) {
          this.viewSettingsStore.persist();
        }
      });
    } else {
      await this.showNotFoundAndRedirect('moneyTracker');
    }
  }

  private async openFocus(id: string) {
    // Wait for focus store to load data
    await this.waitForStoreData(() => {
      const focusItems = Object.keys(this.focusStore.setupsMap());
      return focusItems.length > 0;
    });

    const focus = this.focusStore.setupsMap()[id];
    if (focus && !focus.deletedAt) {
      this.router.navigate(['/today']).then(async () => {
        const { FocusScoresComponent } = await import('@app/components/features/focus-tracker/focus-scores/focus-scores.component');
        this.modalService.openLevel1(FocusScoresComponent, {
          focusId: id,
          dateString: getDateString(new Date()),
        }, { disableClose: true });
        // Enable (unhide) focus feature if hidden - called AFTER opening modal
        if (this.viewSettingsStore.enableFeatureIfHidden('focus', id)) {
          this.viewSettingsStore.persist();
        }
      });
    } else {
      await this.showNotFoundAndRedirect('focusTracker');
    }
  }

  private async openMessageSetup(_id: string) {
    // Navigate to the messages page — the user can find the conversation there.
    // Unlike other entities, messages don't open as a modal overlay.
    await this.router.navigate(['/messages']);
  }

  private readonly entityNotFoundKeyMap: Record<string, { title: string; content: string }> = {
    todo: { title: 'sharedEntityNotFound_todoTitle', content: 'sharedEntityNotFound_todoContent' },
    note: { title: 'sharedEntityNotFound_noteTitle', content: 'sharedEntityNotFound_noteContent' },
    list: { title: 'sharedEntityNotFound_listTitle', content: 'sharedEntityNotFound_listContent' },
    habit: { title: 'sharedEntityNotFound_habitTitle', content: 'sharedEntityNotFound_habitContent' },
    journal: { title: 'sharedEntityNotFound_journalTitle', content: 'sharedEntityNotFound_journalContent' },
    moneyTracker: { title: 'sharedEntityNotFound_moneyTrackerTitle', content: 'sharedEntityNotFound_moneyTrackerContent' },
    focusTracker: { title: 'sharedEntityNotFound_focusTrackerTitle', content: 'sharedEntityNotFound_focusTrackerContent' },
  };

  private async showNotFoundAndRedirect(entityType?: string) {
    const keys = entityType ? this.entityNotFoundKeyMap[entityType] : null;
    const titleKey = keys?.title ?? 'itemNotFound_title';
    const contentKey = keys?.content ?? 'itemNotFound_contentFeature';
    await this.alertService.alert(
      this.ss.t(titleKey),
      this.ss.t(contentKey),
      this.ss.t('common_ok')
    );
    this.router.navigate(['/today']);
  }

  ngOnDestroy() {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
